'use client'

import type {JSX} from 'react'
import type {
    Account,
    AccountProfile,
    Contact,
    ContactLabel,
    KeywordRule,
    ScheduledTask,
    SettingsState,
} from '@/types'
import type {AccountItem} from '@/hooks/useAccounts'
import {AutoReplyView} from '@/views/message/AutoReplyView'
import {ScheduledView} from '@/views/message/ScheduledView'
import {MassMessageView} from '@/views/message/MassMessageView'
import {RealtimeConversationView} from '@/views/conversation/RealtimeConversationView'
import {AddressBookView} from '@/views/contact/AddressBookView'
import {ContactStatusView} from '@/views/contact/ContactStatusView'
import {LabelsView} from '@/views/contact/LabelsView'
import {GroupWelcomeView} from '@/views/group/GroupWelcomeView'
import {GroupAutoReplyView} from '@/views/group/GroupAutoReplyView'
import {GroupMembersView} from '@/views/group/GroupMembersView'
import {PersonalView} from '@/views/settings/PersonalView'
import {AiConfigView} from '@/views/settings/AiConfigView'
import {AutomationView} from '@/views/settings/AutomationView'
import {VoiceChangerView} from '@/views/settings/VoiceChangerView'
import {TTSConfigView} from '@/views/settings/TTSConfigView'
import {ProxySettingsView} from '@/views/settings/ProxySettingsView'
import {WebhookSettingsView} from '@/views/settings/WebhookSettingsView'
import {SystemSettingsView} from '@/views/settings/SystemSettingsView'
import {MomentsView} from '@/views/message/MomentsView'
import {UserManageView} from '@/views/settings/UserManageView'
import {UserProfileView} from '@/views/settings/UserProfileView'
import {QuickReplyView} from '@/views/settings/QuickReplyView'
import {RegistrationSettingsView} from '@/views/settings/RegistrationSettingsView'
import {AdminStatsView} from '@/views/settings/AdminStatsView'
import {PluginMarketView} from '@/views/settings/PluginMarketView'

type ViewRouterProps = {
    activeItemId: string
    account: Account | null
    accounts: AccountItem[]
    contacts: Contact[]
    friends: Contact[]
    groups: Contact[]
    labels: ContactLabel[]
    rules: KeywordRule[]
    tasks: ScheduledTask[]
    settings: SettingsState
    accountProfile: AccountProfile | null
    syncing: boolean
    profileRefreshing: boolean
    saveRule: (rule: Partial<KeywordRule>) => Promise<void> | void
    deleteRule: (id: number) => Promise<void> | void
    saveTask: (task: Partial<ScheduledTask>) => Promise<void> | void
    deleteTask: (id: number) => Promise<void> | void
    refreshTasks: () => Promise<void> | void
    saveSettings: (patch: Partial<SettingsState>) => Promise<void> | void
    syncContacts: (currentAccount?: Account | null) => Promise<void> | void
    resolveContact: (wxid: string) => Promise<Contact | null>
    updateContact: (wxid: string, update: Partial<Contact> | ((contact: Contact) => Contact)) => void
    refreshProfile: () => Promise<void> | void
    logout: () => Promise<void> | void
    systemLogout: () => void
    qrImage: string
    loginStatus: string
    loginLoading: boolean
    loginSuccessVersion: number
    onRefreshQr: (input?: { protocol?: string; proxyUrl?: string; wxid?: string }) => Promise<void> | void
}

export function ViewRouter(props: ViewRouterProps): JSX.Element {
    const {
        activeItemId,
        account,
        accounts,
        contacts,
        friends,
        groups,
        labels,
        rules,
        tasks,
        settings,
        accountProfile,
        syncing,
        profileRefreshing,
        saveRule,
        deleteRule,
        saveTask,
        deleteTask,
        refreshTasks,
        saveSettings,
        syncContacts,
        resolveContact,
        updateContact,
        refreshProfile,
        logout,
        systemLogout,
        qrImage,
        loginStatus,
        loginLoading,
        loginSuccessVersion,
        onRefreshQr,
    } = props

    switch (activeItemId) {
        case 'realtime':
            return (
                <RealtimeConversationView
                    account={account}
                    accounts={accounts}
                    contacts={contacts}
                    resolveContact={resolveContact}
                    updateContact={updateContact}
                    settings={settings}
                />
            )
        case 'auto-reply':
            return (
                <AutoReplyView
                    contacts={friends}
                    labels={labels}
                    settings={settings}
                    rules={rules}
                    saveSettings={saveSettings}
                    saveRule={saveRule}
                    deleteRule={deleteRule}
                />
            )
        case 'scheduled':
            return (
                <ScheduledView
                    contacts={friends}
                    labels={labels}
                    tasks={tasks}
                    onSaveTask={saveTask}
                    onDeleteTask={deleteTask}
                    onRefresh={refreshTasks}
                    voiceId={settings.tts?.selectedVoiceId}
                />
            )
        case 'mass':
            return <MassMessageView account={account} contacts={friends} labels={labels}/>
        case 'address-book':
            return (
                <AddressBookView
                    contacts={friends}
                    account={account}
                    syncContacts={syncContacts}
                    syncing={syncing}
                />
            )
        case 'check-status':
            return <ContactStatusView contacts={friends} account={account}/>
        case 'labels':
            return <LabelsView account={account} contacts={friends}/>
        case 'welcome':
            return <GroupWelcomeView groups={groups} settings={settings} saveSettings={saveSettings}/>
        case 'group-auto-reply':
            return <GroupAutoReplyView groups={groups} settings={settings} saveSettings={saveSettings}/>
        case 'members':
            return <GroupMembersView groups={groups} account={account}/>
        case 'personal':
            return (
                <PersonalView
                    account={account}
                    profile={accountProfile}
                    contacts={contacts}
                    logout={logout}
                    refreshProfile={refreshProfile}
                    profileRefreshing={profileRefreshing}
                    qrImage={qrImage}
                    loginStatus={loginStatus}
                    loginLoading={loginLoading}
                    loginSuccessVersion={loginSuccessVersion}
                    onRefreshQr={onRefreshQr}
                />
            )
        case 'ai-config':
            return <AiConfigView settings={settings} saveSettings={saveSettings}/>
        case 'automation':
            return <AutomationView settings={settings} saveSettings={saveSettings}/>
        case 'system':
            return <SystemSettingsView settings={settings} saveSettings={saveSettings}/>
        case 'voice-changer':
            return <VoiceChangerView settings={settings} saveSettings={saveSettings}/>
        case 'tts-config':
            return <TTSConfigView settings={settings} saveSettings={saveSettings}/>
        case 'proxy':
            return <ProxySettingsView settings={settings} saveSettings={saveSettings} account={account}/>
        case 'webhook-trigger':
            return <WebhookSettingsView account={account} accounts={accounts} contacts={friends} groups={groups} labels={labels}/>
        case 'plugin-market':
            return <PluginMarketView account={account}/>
        case 'moments':
            return <MomentsView account={account} contacts={friends} labels={labels} resolveContact={resolveContact}/>
        case 'user-manage':
            return <UserManageView/>
        case 'admin-stats':
            return <AdminStatsView/>
        case 'registration-settings':
            return <RegistrationSettingsView/>
        case 'user-profile':
            return <UserProfileView onLogout={systemLogout}/>
        case 'quick-reply':
            return <QuickReplyView/>
        default:
            return (
                <AutoReplyView
                    contacts={friends}
                    labels={labels}
                    settings={settings}
                    rules={rules}
                    saveSettings={saveSettings}
                    saveRule={saveRule}
                    deleteRule={deleteRule}
                />
            )
    }
}
